import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Play,
  Pause,
  Clock,
  Calendar,
  BrainCircuit,
  Search,
  CheckCircle2,
  Users,
  BarChart3,
  Sparkles,
  Download,
  Share2,
  User,
  CheckSquare,
  FileText,
  Activity,
  Zap,
  MoreHorizontal,
  FileAudio,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import toast from 'react-hot-toast'
import { jsPDF } from 'jspdf'
import { recordingsService } from '@/services/firebase.service'
import { httpsCallable } from 'firebase/functions'
import { functions, auth } from '@/lib/firebase'

export { Landing } from './Landing'

// Type for dashboard recordings
interface DashboardCall {
  id: string
  title: string
  participants: string[]
  duration: string
  date: string
  status: 'Processed' | 'Processing' | 'Error'
  tag: string
  priority: 'High' | 'Medium' | 'Low'
}

// Helper functions
const formatDuration = (seconds: number) => {
  if (!seconds) return '0m'
  const mins = Math.floor(seconds / 60)
  const hours = Math.floor(mins / 60)
  if (hours > 0) return `${hours}h ${mins % 60}m`
  return `${mins}m`
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
  if (diffDays === 1) return 'Ieri'
  if (diffDays < 7) return `${diffDays} giorni fa`
  return date.toLocaleDateString('it-IT')
}

const aiInsights = [
  { type: 'alert', text: 'Conflitto di budget rilevato nella discussione "Marketing".', confidence: 98 },
  { type: 'action', text: 'Assegnare task: Review contrattuale entro Venerdì.', confidence: 92 },
  { type: 'info', text: 'Il sentiment generale è migliorato del 15% rispetto alla scorsa settimana.', confidence: 85 },
]

// Usage stats type
interface UsageStats {
  totalRecordings: number
  completedRecordings: number
  totalMinutes: number
  voiceProfiles: number
  activeVoiceProfiles: number
  totalActionItems: number
  completedActionItems: number
  openActionItems: number
  apiUsage: {
    openaiTokens: number
    replicateMinutes: number
    storageBytes: number
  }
}

export function Dashboard() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [isGeneratingReport, setIsGeneratingReport] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [recentCalls, setRecentCalls] = useState<DashboardCall[]>([])
  const [isLoadingCalls, setIsLoadingCalls] = useState(true)
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null)
  const [isLoadingStats, setIsLoadingStats] = useState(true)

  // Load usage stats from Cloud Function
  useEffect(() => {
    if (!user) return

    const fetchStats = async () => {
      try {
        setIsLoadingStats(true)
        const getUsageStats = httpsCallable<void, UsageStats>(functions, 'getUsageStats')
        const result = await getUsageStats()
        setUsageStats(result.data)
      } catch (error) {
        console.error('Error fetching usage stats:', error)
      } finally {
        setIsLoadingStats(false)
      }
    }

    fetchStats()
    // Refresh stats every 30 seconds
    const interval = setInterval(fetchStats, 30000)
    return () => clearInterval(interval)
  }, [user])

  // Load real recordings from Firebase
  useEffect(() => {
    const firebaseUid = auth.currentUser?.uid
    if (!user || !firebaseUid) return

    setIsLoadingCalls(true)
    const unsubscribe = recordingsService.subscribeAll(firebaseUid, (firebaseRecordings) => {
      const formattedCalls: DashboardCall[] = firebaseRecordings
        .slice(0, 5) // Show only last 5
        .map(r => ({
          id: r.id,
          title: r.title || r.fileName || 'Registrazione',
          participants: r.speakers?.map(s => s.name?.substring(0, 2).toUpperCase() || 'SP') || ['--'],
          duration: formatDuration(r.duration),
          date: formatDate(r.createdAt),
          status: r.status === 'completed' ? 'Processed' : r.status === 'error' ? 'Error' : 'Processing',
          tag: 'Meeting',
          priority: 'Medium' as const,
        }))
      setRecentCalls(formattedCalls)
      setIsLoadingCalls(false)
    })

    return () => unsubscribe()
  }, [user])

  const handleExportAnalytics = () => {
    toast.loading('Generazione export in corso...', { id: 'export' })
    setTimeout(() => {
      // Generate CSV with analytics data
      const csvContent = [
        'Metrica,Valore,Target,Percentuale',
        'Minuti Processati,1240,2000,62%',
        'Action Items Totali,84,-,-',
        'Action Items Aperti,12,-,-',
        'Voice ID Attivi,5,10,50%',
        'Precisione AI,99.2%,95%,104%',
        '',
        'Riunione,Durata,Data,Status,Tag,Priorità',
        ...recentCalls.map(r => `"${r.title}","${r.duration}","${r.date}","${r.status}","${r.tag}","${r.priority}"`)
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `NexScribe_Analytics_${new Date().toISOString().split('T')[0]}.csv`
      link.click()
      URL.revokeObjectURL(url)

      toast.success('Export Analytics scaricato!', { id: 'export' })
    }, 800)
  }

  const handleGenerateReport = () => {
    setIsGeneratingReport(true)
    toast.loading('AI sta generando il report...', { id: 'report' })

    // Simulate AI processing delay then generate real PDF
    setTimeout(() => {
      try {
        const doc = new jsPDF()
        const pageWidth = doc.internal.pageSize.getWidth()
        const margin = 20
        let y = margin

        // Header
        doc.setFontSize(24)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(30, 58, 138) // Blue color
        doc.text('NexScribe', margin, y)
        y += 8
        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(100)
        doc.text('EXECUTIVE REPORT', margin, y)
        y += 15

        // Date
        doc.setFontSize(10)
        doc.setTextColor(100)
        const today = new Date().toLocaleDateString('it-IT', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
        doc.text(`Generato il ${today}`, margin, y)
        y += 15

        // Summary Section
        doc.setFontSize(14)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(30, 41, 59)
        doc.text('Executive Summary', margin, y)
        y += 8

        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(71, 85, 105)
        const summaryText = `Questa settimana sono state processate ${recentCalls.length} riunioni per un totale di circa 2 ore e 57 minuti di contenuto audio. L'AI ha identificato ${aiInsights.length} insight critici che richiedono attenzione.`
        const summaryLines = doc.splitTextToSize(summaryText, pageWidth - 2 * margin)
        summaryLines.forEach((line: string) => {
          doc.text(line, margin, y)
          y += 5
        })
        y += 10

        // KPI Section
        doc.setFontSize(14)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(30, 41, 59)
        doc.text('KPI Overview', margin, y)
        y += 8

        const kpis = [
          { label: 'Minuti Processati', value: '1,240 / 2,000' },
          { label: 'Action Items', value: '84 (12 aperti)' },
          { label: 'Voice ID Attivi', value: '5' },
          { label: 'Precisione AI', value: '99.2%' },
        ]
        doc.setFontSize(10)
        kpis.forEach(kpi => {
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(71, 85, 105)
          doc.text(`• ${kpi.label}:`, margin, y)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(30, 41, 59)
          doc.text(kpi.value, margin + 60, y)
          y += 6
        })
        y += 10

        // AI Insights Section
        doc.setFontSize(14)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(30, 41, 59)
        doc.text('AI Insights Critici', margin, y)
        y += 8

        doc.setFontSize(10)
        aiInsights.forEach((insight, index) => {
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(
            insight.type === 'alert' ? 220 : insight.type === 'action' ? 37 : 34,
            insight.type === 'alert' ? 38 : insight.type === 'action' ? 99 : 197,
            insight.type === 'alert' ? 38 : insight.type === 'action' ? 235 : 94
          )
          doc.text(`${index + 1}. [${insight.type.toUpperCase()}]`, margin, y)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(71, 85, 105)
          const insightLines = doc.splitTextToSize(insight.text, pageWidth - 2 * margin - 30)
          insightLines.forEach((line: string, lineIdx: number) => {
            doc.text(line, margin + (lineIdx === 0 ? 35 : 0), y)
            if (lineIdx < insightLines.length - 1) y += 5
          })
          doc.setTextColor(100)
          doc.text(`(${insight.confidence}% confidence)`, pageWidth - margin - 30, y)
          y += 8
        })
        y += 10

        // Recent Recordings Section
        doc.setFontSize(14)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(30, 41, 59)
        doc.text('Riunioni Recenti', margin, y)
        y += 8

        doc.setFontSize(10)
        recentCalls.forEach((call, index) => {
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(30, 41, 59)
          doc.text(`${index + 1}. ${call.title}`, margin, y)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(100)
          doc.text(`${call.duration} • ${call.date} • ${call.tag}`, margin + 5, y + 5)
          const statusColor = call.status === 'Processed' ? { r: 34, g: 197, b: 94 } : { r: 245, g: 158, b: 11 }
          doc.setTextColor(statusColor.r, statusColor.g, statusColor.b)
          doc.text(call.status, pageWidth - margin - 25, y)
          y += 12
        })

        // Footer
        const pageHeight = doc.internal.pageSize.getHeight()
        doc.setFontSize(8)
        doc.setTextColor(150)
        doc.text('NexScribe Enterprise • Report generato automaticamente', pageWidth / 2, pageHeight - 10, { align: 'center' })

        // Save
        const fileName = `NexScribe_Report_${new Date().toISOString().split('T')[0]}.pdf`
        doc.save(fileName)

        setIsGeneratingReport(false)
        toast.success('Report PDF scaricato!', { id: 'report' })
      } catch (error) {
        console.error('Error generating report:', error)
        setIsGeneratingReport(false)
        toast.error('Errore nella generazione del report', { id: 'report' })
      }
    }, 1500)
  }

  const filteredCalls = recentCalls.filter(call =>
    call.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    call.tag.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="min-h-full relative space-y-8 pb-10">

      {/* Ambient Background Glow Effects */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-indigo-400/20 rounded-full blur-[100px]"></div>
        <div className="absolute top-1/3 -left-40 w-80 h-80 bg-purple-400/15 rounded-full blur-[80px]"></div>
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-pink-400/10 rounded-full blur-[90px]"></div>
      </div>

      <div className="relative space-y-8">
        {/* HEADER SECTION */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pt-2">
            <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">
                Command Center
            </h1>
            <p className="text-slate-500 mt-1 flex items-center gap-2 text-sm font-medium">
                Bentornato, {user?.name?.split(' ')[0] || 'Admin'}.
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200">
                System Online
                </span>
            </p>
            </div>
            <div className="flex items-center gap-3">
                <button
                  onClick={handleExportAnalytics}
                  className="glass-button px-5 py-2.5 text-slate-700 text-sm flex items-center gap-2"
                >
                    <Download className="h-4 w-4" /> Export Analytics
                </button>
                <button
                  onClick={() => navigate('/upload')}
                  className="bg-gradient-to-r from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700 text-white px-5 py-2.5 rounded-2xl text-sm font-medium shadow-lg shadow-slate-900/30 transition-all flex items-center gap-2 hover:-translate-y-0.5"
                >
                    <Play className="h-4 w-4 fill-current" /> Nuova Registrazione
                </button>
            </div>
        </div>

        {/* KPI CARDS - LIQUID GLASS STYLE */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
            {
              label: 'Minuti Processati',
              value: isLoadingStats ? '...' : usageStats?.totalMinutes?.toLocaleString() || '0',
              sub: `/ ${user?.minutesLimit || 60}`,
              icon: Clock,
              color: 'text-blue-600',
              bg: 'bg-blue-100/80',
              chart: [40, 35, 55, 70, 60, 85, 90]
            },
            {
              label: 'Action Items',
              value: isLoadingStats ? '...' : usageStats?.totalActionItems?.toString() || '0',
              sub: `${usageStats?.openActionItems || 0} open`,
              icon: CheckCircle2,
              color: 'text-emerald-600',
              bg: 'bg-emerald-100/80',
              chart: [20, 40, 30, 45, 60, 55, 80]
            },
            {
              label: 'Voice ID Attivi',
              value: isLoadingStats ? '...' : usageStats?.activeVoiceProfiles?.toString() || '0',
              sub: `/ ${usageStats?.voiceProfiles || 0} totali`,
              icon: Users,
              color: 'text-violet-600',
              bg: 'bg-violet-100/80',
              chart: [10, 10, 20, 20, 40, 40, 50]
            },
            {
              label: 'Registrazioni',
              value: isLoadingStats ? '...' : usageStats?.completedRecordings?.toString() || '0',
              sub: `/ ${usageStats?.totalRecordings || 0} totali`,
              icon: BrainCircuit,
              color: 'text-amber-600',
              bg: 'bg-amber-100/80',
              chart: [90, 92, 91, 95, 96, 98, 99]
            },
            ].map((stat, i) => (
            <motion.div
                key={i}
                whileHover={{ y: -6, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
                className="glass-panel p-6 group liquid-glow cursor-default"
            >
                <div className="flex justify-between items-start mb-4">
                <div className={`p-3 rounded-2xl ${stat.bg} backdrop-blur-sm shadow-sm`}>
                    <stat.icon className={`h-5 w-5 ${stat.color}`} />
                </div>
                {/* Sparkline Visual */}
                <div className="flex items-end gap-1 h-8">
                    {stat.chart.map((h, idx) => (
                        <div key={idx} className={`w-1.5 rounded-full opacity-30 group-hover:opacity-60 transition-all duration-300 ${stat.color.replace('text', 'bg')}`} style={{ height: `${h}%` }}></div>
                    ))}
                </div>
                </div>
                <div>
                <h3 className="text-3xl font-bold text-slate-900 tracking-tight">{stat.value}</h3>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mt-1">{stat.label}</p>
                </div>
            </motion.div>
            ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* MAIN COLUMN: RECENT RECORDINGS */}
            <div className="lg:col-span-2 space-y-6">
            <div className="glass-panel overflow-hidden">
                <div className="p-6 border-b border-white/30 flex justify-between items-center">
                <div>
                    <h2 className="font-bold text-lg text-slate-900">Live Feed</h2>
                    <p className="text-xs text-slate-500 font-medium">Flusso dati in tempo reale</p>
                </div>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Ricerca semantica..."
                      className="glass-input pl-9 pr-4 py-2.5 text-sm w-48 transition-all focus:w-64 placeholder:text-slate-400"
                    />
                </div>
                </div>

                <div className="divide-y divide-slate-50">
                {isLoadingCalls ? (
                  <div className="p-8 text-center text-slate-400">
                    <Activity className="h-8 w-8 mx-auto mb-2 opacity-50 animate-pulse" />
                    <p className="text-sm">Caricamento registrazioni...</p>
                  </div>
                ) : filteredCalls.length === 0 && searchQuery ? (
                  <div className="p-8 text-center text-slate-400">
                    <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Nessun risultato per "{searchQuery}"</p>
                  </div>
                ) : filteredCalls.length === 0 ? (
                  <div className="p-8 text-center text-slate-400">
                    <FileAudio className="h-10 w-10 mx-auto mb-3 opacity-50" />
                    <p className="text-sm font-medium mb-2">Nessuna registrazione</p>
                    <p className="text-xs mb-4">Carica il tuo primo file audio per iniziare.</p>
                    <button
                      onClick={() => navigate('/upload')}
                      className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2"
                    >
                      <FileAudio className="h-4 w-4" /> Nuova Registrazione
                    </button>
                  </div>
                ) : (
                  filteredCalls.map((call) => (
                    <div
                      key={call.id}
                      onClick={() => call.status !== 'Processing' && navigate(`/recordings/${call.id}`)}
                      className={`p-5 hover:bg-slate-50 transition-all group ${call.status !== 'Processing' ? 'cursor-pointer' : 'cursor-wait'}`}
                    >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-5">
                        {/* Icona Status */}
                        <div className={`h-12 w-12 rounded-2xl flex items-center justify-center text-lg font-bold shadow-sm ring-1 ring-inset ring-black/5 ${
                            call.status === 'Processing' ? 'bg-amber-50 text-amber-600' : 'bg-white text-slate-700'
                        }`}>
                            {call.status === 'Processing' ? <Activity className="h-6 w-6 animate-pulse" /> : call.title.substring(0, 2).toUpperCase()}
                        </div>

                        <div>
                            <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-slate-900 text-base group-hover:text-blue-600 transition-colors">{call.title}</h4>
                                {call.priority === 'High' && <span className="h-2 w-2 rounded-full bg-red-500" title="Priorità Alta"></span>}
                            </div>
                            <div className="flex items-center gap-4 mt-1.5">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-200 uppercase tracking-wide">{call.tag}</span>
                                <span className="text-xs text-slate-400 flex items-center gap-1 font-medium"><Clock className="h-3 w-3" /> {call.duration}</span>
                                <span className="text-xs text-slate-400 flex items-center gap-1 font-medium"><Calendar className="h-3 w-3" /> {call.date}</span>
                            </div>
                        </div>
                        </div>

                        <div className="flex items-center gap-4">
                        {call.status === 'Processing' ? (
                            <div className="flex items-center gap-2 bg-amber-50 text-amber-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-amber-100">
                                <div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-bounce"></div> Analyzing
                            </div>
                        ) : (
                            <div className="bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-emerald-100 flex items-center gap-1.5">
                                <CheckCircle2 className="h-3.5 w-3.5" /> Ready
                            </div>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            if (call.status === 'Processed') {
                              const link = `${window.location.origin}/recordings/${call.id}`
                              navigator.clipboard.writeText(link)
                              toast.success('Link copiato negli appunti!')
                            } else {
                              toast('Attendere il completamento...', { icon: '⏳' })
                            }
                          }}
                          className="text-slate-300 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-lg transition-colors"
                          title="Copia link"
                        >
                            <MoreHorizontal className="h-5 w-5" />
                        </button>
                        </div>
                    </div>
                    </div>
                  ))
                )}
                </div>
            </div>
            </div>

            {/* SIDE COLUMN: AI INTELLIGENCE (Dark Glass Theme) */}
            <div className="space-y-6">
            {/* Dark Glass Panel for AI */}
            <div className="glass-panel-dark overflow-hidden relative text-white">
                {/* Glow Effects */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/30 blur-[100px] rounded-full pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/20 blur-[80px] rounded-full pointer-events-none"></div>

                <div className="p-6 relative z-10">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/10 rounded-lg backdrop-blur-md border border-white/10">
                                <BrainCircuit className="h-5 w-5 text-emerald-400" />
                            </div>
                            <div>
                                <h3 className="font-bold text-white text-sm">NexScribe AI</h3>
                                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Live Analysis</p>
                            </div>
                        </div>
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
                        </span>
                    </div>

                    <div className="space-y-3">
                        {aiInsights.map((insight, i) => (
                            <div key={i} className="group bg-white/5 border border-white/5 hover:border-white/20 rounded-xl p-4 backdrop-blur-sm hover:bg-white/10 transition-all cursor-default">
                                <div className="flex justify-between items-start mb-2">
                                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${
                                        insight.type === 'alert' ? 'bg-red-500/20 text-red-300 border-red-500/30' :
                                        insight.type === 'action' ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' :
                                        'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                                    }`}>{insight.type}</span>
                                    <span className="text-xs font-mono text-slate-500 group-hover:text-white transition-colors">{insight.confidence}%</span>
                                </div>
                                <p className="text-sm text-slate-300 leading-relaxed group-hover:text-white transition-colors">
                                    {insight.text}
                                </p>
                            </div>
                        ))}
                    </div>

                    <button
                      onClick={handleGenerateReport}
                      disabled={isGeneratingReport}
                      className="w-full mt-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl text-sm font-bold shadow-lg shadow-blue-900/50 transition-all flex items-center justify-center gap-2 border-t border-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isGeneratingReport ? (
                          <>
                            <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Generazione in corso...
                          </>
                        ) : (
                          <>
                            <Zap className="h-4 w-4 fill-current" /> Genera Report Esecutivo
                          </>
                        )}
                    </button>
                </div>
            </div>

            {/* System Status Mini Widget - Glass Style */}
            <div className="glass-panel p-5">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Stato Risorse</h3>
                <div className="space-y-4">
                    <div>
                        <div className="flex justify-between text-xs mb-2">
                            <span className="text-slate-700 font-medium">Minuti Audio</span>
                            <span className="text-slate-900 font-bold">
                              {usageStats?.totalMinutes || 0} / {user?.minutesLimit || 60} min
                            </span>
                        </div>
                        <div className="h-2 w-full bg-white/50 backdrop-blur-sm rounded-full overflow-hidden shadow-inner">
                            <div
                              className="h-full bg-gradient-to-r from-slate-700 to-slate-500 rounded-full transition-all"
                              style={{ width: `${Math.min(100, ((usageStats?.totalMinutes || 0) / (user?.minutesLimit || 60)) * 100)}%` }}
                            />
                        </div>
                    </div>
                    <div>
                        <div className="flex justify-between text-xs mb-2">
                            <span className="text-slate-700 font-medium">Voice ID</span>
                            <span className="text-slate-900 font-bold">
                              {usageStats?.voiceProfiles || 0} / {user?.voiceIdsLimit || 3}
                            </span>
                        </div>
                        <div className="h-2 w-full bg-white/50 backdrop-blur-sm rounded-full overflow-hidden shadow-inner">
                            <div
                              className="h-full bg-gradient-to-r from-blue-600 to-indigo-500 rounded-full transition-all"
                              style={{ width: `${Math.min(100, ((usageStats?.voiceProfiles || 0) / (user?.voiceIdsLimit || 3)) * 100)}%` }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            </div>
        </div>
      </div>
    </div>
  )
}

import { useParams, useNavigate as useNav } from 'react-router-dom'
import { ArrowLeft, Copy, Check as CheckIcon, X, MessageSquare, Loader2, Volume2, VolumeX, Edit3, Fingerprint, Link2 } from 'lucide-react'
import { useRef } from 'react'
import { voiceProfilesService } from '@/services/firebase.service'
import { useAuthStore as useAuthStoreDetail, type VoiceProfile as VoiceProfileType } from '@/store/auth'
import type { Recording } from '@/store/auth'

// Speaker colors for display
const speakerColors = [
  'bg-blue-100 text-blue-700',
  'bg-emerald-100 text-emerald-700',
  'bg-purple-100 text-purple-700',
  'bg-pink-100 text-pink-700',
  'bg-amber-100 text-amber-700',
  'bg-cyan-100 text-cyan-700',
  'bg-rose-100 text-rose-700',
  'bg-indigo-100 text-indigo-700',
]

// Helper to format time from seconds to MM:SS
const formatTimeFromSeconds = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

// Helper to format duration
const formatDurationMinutes = (seconds: number): string => {
  if (!seconds) return '0 min'
  const mins = Math.floor(seconds / 60)
  if (mins >= 60) {
    const hours = Math.floor(mins / 60)
    return `${hours}h ${mins % 60}m`
  }
  return `${mins} min`
}

// Helper to format date
const formatRecordingDate = (dateStr: string): string => {
  const date = new Date(dateStr)
  return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })
}

// Transform Firestore data to display format
interface DisplayTranscript {
  id: number
  speakerId: string
  speaker: string
  time: string
  text: string
  color: string
  hasVoiceProfile: boolean
}

interface DisplayActionItem {
  id: number
  text: string
  owner: string
  due: string
  completed: boolean
}

const transformRecordingData = (recording: Recording): {
  title: string
  date: string
  duration: string
  participants: number
  transcript: DisplayTranscript[]
  summary: string
  actionItems: DisplayActionItem[]
  sentiment: number
  topics: string[]
} => {
  // Build speaker map for colors and names
  const speakerMap = new Map<string, { name: string; color: string; hasVoiceProfile: boolean }>()
  recording.speakers?.forEach((speaker, index) => {
    speakerMap.set(speaker.id, {
      name: speaker.name || speaker.label || `Speaker ${index + 1}`,
      color: speakerColors[index % speakerColors.length],
      hasVoiceProfile: !!speaker.voiceId,
    })
  })

  // Merge consecutive segments into readable paragraphs
  // Group segments by time gaps (merge if gap < 2 seconds) or by ~30 second chunks
  const mergedTranscript: DisplayTranscript[] = []

  if (recording.transcript && recording.transcript.length > 0) {
    let currentParagraph: {
      speakerId: string
      startTime: number
      texts: string[]
    } | null = null

    const MAX_PARAGRAPH_DURATION = 30 // seconds - max length before forcing new paragraph
    const MAX_GAP = 2 // seconds - gap that triggers new paragraph

    recording.transcript.forEach((segment, index) => {
      const isNewParagraph =
        !currentParagraph ||
        segment.speakerId !== currentParagraph.speakerId ||
        segment.startTime - currentParagraph.startTime > MAX_PARAGRAPH_DURATION ||
        (index > 0 && segment.startTime - recording.transcript![index - 1].endTime > MAX_GAP)

      if (isNewParagraph) {
        // Save previous paragraph
        if (currentParagraph) {
          const speaker = speakerMap.get(currentParagraph.speakerId) || { name: 'Speaker', color: speakerColors[0], hasVoiceProfile: false }
          mergedTranscript.push({
            id: mergedTranscript.length + 1,
            speakerId: currentParagraph.speakerId,
            speaker: speaker.name,
            time: formatTimeFromSeconds(currentParagraph.startTime),
            text: currentParagraph.texts.join(' ').trim(),
            color: speaker.color,
            hasVoiceProfile: speaker.hasVoiceProfile,
          })
        }
        // Start new paragraph
        currentParagraph = {
          speakerId: segment.speakerId,
          startTime: segment.startTime,
          texts: [segment.text.trim()],
        }
      } else {
        // Add to current paragraph
        currentParagraph!.texts.push(segment.text.trim())
      }
    })

    // Don't forget the last paragraph
    if (currentParagraph !== null) {
      const lastParagraph = currentParagraph as { speakerId: string; startTime: number; texts: string[] }
      const speaker = speakerMap.get(lastParagraph.speakerId) || { name: 'Speaker', color: speakerColors[0], hasVoiceProfile: false }
      mergedTranscript.push({
        id: mergedTranscript.length + 1,
        speakerId: lastParagraph.speakerId,
        speaker: speaker.name,
        time: formatTimeFromSeconds(lastParagraph.startTime),
        text: lastParagraph.texts.join(' ').trim(),
        color: speaker.color,
        hasVoiceProfile: speaker.hasVoiceProfile,
      })
    }
  }

  const transcript = mergedTranscript

  // Transform action items
  const actionItems: DisplayActionItem[] = recording.actionItems?.map((item, index) => ({
    id: index + 1,
    text: item.text,
    owner: item.owner || 'Non assegnato',
    due: item.dueDate || 'Da definire',
    completed: item.completed,
  })) || []

  // Calculate sentiment as percentage (positive=80, neutral=50, negative=20)
  const sentimentMap = { positive: 80, neutral: 50, negative: 20 }
  const sentiment = recording.analysis?.sentiment
    ? sentimentMap[recording.analysis.sentiment] || 50
    : 50

  return {
    title: recording.title || recording.fileName || 'Registrazione',
    date: formatRecordingDate(recording.createdAt),
    duration: formatDurationMinutes(recording.duration),
    participants: recording.speakers?.length || 1,
    transcript,
    summary: recording.analysis?.summary || recording.summary || 'Analisi AI in elaborazione...',
    actionItems,
    sentiment,
    topics: recording.analysis?.topics || [],
  }
}

export function RecordingDetail() {
  const { id } = useParams<{ id: string }>()
  const navigateBack = useNav()
  const { user: authUser } = useAuthStoreDetail()

  // State for Firestore data
  const [firestoreRecording, setFirestoreRecording] = useState<Recording | null>(null)
  const [isLoadingRecording, setIsLoadingRecording] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Fetch recording from Firestore
  useEffect(() => {
    if (!id) {
      setLoadError('ID registrazione non valido')
      setIsLoadingRecording(false)
      return
    }

    setIsLoadingRecording(true)
    setLoadError(null)

    // Subscribe to real-time updates
    const unsubscribe = recordingsService.subscribe(id, (recording) => {
      if (recording) {
        setFirestoreRecording(recording)
        setLoadError(null)
      } else {
        setLoadError('Registrazione non trovata')
      }
      setIsLoadingRecording(false)
    })

    return () => unsubscribe()
  }, [id])

  // Transform data for display
  const recording = firestoreRecording
    ? transformRecordingData(firestoreRecording)
    : {
        title: 'Caricamento...',
        date: '',
        duration: '',
        participants: 0,
        transcript: [],
        summary: '',
        actionItems: [],
        sentiment: 0,
        topics: [],
      }

  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [activeTab, setActiveTab] = useState<'summary' | 'notes'>('summary')
  const [searchQuery, setSearchQuery] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const [actionItems, setActionItems] = useState<DisplayActionItem[]>([])
  const [notes, setNotes] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [audioError, setAudioError] = useState<string | null>(null)

  // Speaker renaming state
  const [editingSpeaker, setEditingSpeaker] = useState<{ id: string; name: string; voiceId?: string } | null>(null)
  const [newSpeakerName, setNewSpeakerName] = useState('')
  const [selectedVoiceProfileId, setSelectedVoiceProfileId] = useState<string | null>(null)
  const [voiceProfiles, setVoiceProfiles] = useState<VoiceProfileType[]>([])

  // Audio element ref
  const audioRef = useRef<HTMLAudioElement>(null)

  // Update actionItems when recording changes
  useEffect(() => {
    if (recording.actionItems.length > 0) {
      setActionItems(recording.actionItems)
    }
  }, [firestoreRecording])

  // Set duration from recording data
  useEffect(() => {
    if (firestoreRecording?.duration) {
      setDuration(firestoreRecording.duration)
    }
  }, [firestoreRecording])

  // Handle audio play/pause
  const togglePlayPause = () => {
    if (!audioRef.current) return

    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play().catch(err => {
        console.error('Error playing audio:', err)
        setAudioError('Impossibile riprodurre l\'audio')
      })
    }
    setIsPlaying(!isPlaying)
  }

  // Handle audio time update
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime)
    }
  }

  // Handle audio loaded metadata
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration)
      setAudioError(null)
    }
  }

  // Handle audio ended
  const handleAudioEnded = () => {
    setIsPlaying(false)
    setCurrentTime(0)
  }

  // Handle seek
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !duration || !isFinite(duration) || duration === 0) return

    const rect = e.currentTarget.getBoundingClientRect()
    const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    const newTime = percent * duration

    if (isFinite(newTime) && newTime >= 0) {
      audioRef.current.currentTime = newTime
      setCurrentTime(newTime)
    }
  }

  // Toggle mute
  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted
      setIsMuted(!isMuted)
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const handleExportPDF = () => {
    setIsExporting(true)
    toast.loading('Generazione PDF in corso...', { id: 'export-pdf' })

    try {
      const doc = new jsPDF()
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 20
      const contentWidth = pageWidth - 2 * margin
      let y = margin

      // Helper to add new page if needed
      const checkNewPage = (neededHeight: number) => {
        if (y + neededHeight > pageHeight - margin) {
          doc.addPage()
          y = margin
          return true
        }
        return false
      }

      // Title
      doc.setFontSize(20)
      doc.setFont('helvetica', 'bold')
      doc.text(recording.title, margin, y)
      y += 10

      // Metadata
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(100)
      doc.text(`${recording.date} • ${recording.duration} • ${recording.participants} Partecipanti`, margin, y)
      y += 15
      doc.setTextColor(0)

      // Summary Section
      if (recording.summary) {
        doc.setFontSize(14)
        doc.setFont('helvetica', 'bold')
        doc.text('Executive Summary', margin, y)
        y += 8

        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        const summaryLines = doc.splitTextToSize(recording.summary, contentWidth)
        summaryLines.forEach((line: string) => {
          checkNewPage(6)
          doc.text(line, margin, y)
          y += 5
        })
        y += 10
      }

      // Action Items Section
      if (actionItems.length > 0) {
        checkNewPage(20)
        doc.setFontSize(14)
        doc.setFont('helvetica', 'bold')
        doc.text('Action Items', margin, y)
        y += 8

        doc.setFontSize(10)
        actionItems.forEach((item, _index) => {
          checkNewPage(12)
          doc.setFont('helvetica', 'normal')
          const checkbox = item.completed ? '[x]' : '[ ]'
          const itemText = `${checkbox} ${item.text}`
          const lines = doc.splitTextToSize(itemText, contentWidth - 10)
          lines.forEach((line: string, lineIndex: number) => {
            doc.text(line, margin + (lineIndex === 0 ? 0 : 15), y)
            y += 5
          })
          doc.setTextColor(100)
          doc.text(`   Responsabile: ${item.owner} • Scadenza: ${item.due}`, margin + 15, y)
          doc.setTextColor(0)
          y += 7
        })
        y += 5
      }

      // Topics Section
      if (recording.topics.length > 0) {
        checkNewPage(15)
        doc.setFontSize(14)
        doc.setFont('helvetica', 'bold')
        doc.text('Topic Principali', margin, y)
        y += 8

        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        doc.text(recording.topics.join(', '), margin, y)
        y += 15
      }

      // Transcript Section
      checkNewPage(20)
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.text('Trascrizione', margin, y)
      y += 10

      doc.setFontSize(9)
      recording.transcript.forEach((block) => {
        checkNewPage(15)

        // Timestamp
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(100)
        doc.text(`[${block.time}]`, margin, y)
        doc.setTextColor(0)

        // Text
        doc.setFont('helvetica', 'normal')
        const textLines = doc.splitTextToSize(block.text, contentWidth - 15)
        textLines.forEach((line: string, lineIndex: number) => {
          if (lineIndex === 0) {
            doc.text(line, margin + 20, y)
          } else {
            checkNewPage(5)
            doc.text(line, margin + 20, y)
          }
          y += 4.5
        })
        y += 3
      })

      // Footer
      const totalPages = doc.getNumberOfPages()
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i)
        doc.setFontSize(8)
        doc.setTextColor(150)
        doc.text(
          `NexScribe • Pagina ${i} di ${totalPages}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        )
      }

      // Save
      const fileName = `${recording.title.replace(/[^a-z0-9]/gi, '_')}_trascrizione.pdf`
      doc.save(fileName)

      setIsExporting(false)
      toast.success('PDF esportato con successo!', { id: 'export-pdf' })
    } catch (error) {
      console.error('Error generating PDF:', error)
      setIsExporting(false)
      toast.error('Errore nella generazione del PDF', { id: 'export-pdf' })
    }
  }

  const handleShare = () => {
    const link = `${window.location.origin}/recordings/${id}`
    navigator.clipboard.writeText(link)
    toast.success('Link copiato negli appunti!')
  }

  const handleCopyTranscript = () => {
    const text = recording.transcript.map(b => `[${b.time}] ${b.speaker}: ${b.text}`).join('\n')
    navigator.clipboard.writeText(text)
    toast.success('Trascrizione copiata!')
  }

  const toggleActionItem = (itemId: number) => {
    setActionItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, completed: !item.completed } : item
    ))
    const item = actionItems.find(i => i.id === itemId)
    if (item) {
      toast.success(item.completed ? 'Task riaperto' : 'Task completato!')
    }
  }

  const handleSaveNotes = () => {
    toast.success('Note salvate!')
  }

  // Handle speaker rename
  const handleRenameSpeaker = async () => {
    if (!editingSpeaker || !newSpeakerName.trim() || !firestoreRecording || !id) return

    try {
      toast.loading('Salvataggio...', { id: 'rename-speaker' })

      // Update the speakers array with the new name and optional voiceId
      const updatedSpeakers = firestoreRecording.speakers?.map(s =>
        s.id === editingSpeaker.id
          ? {
              ...s,
              name: newSpeakerName.trim(),
              voiceId: selectedVoiceProfileId || undefined
            }
          : s
      )

      await recordingsService.update(id, {
        speakers: updatedSpeakers
      })

      const linkedProfile = selectedVoiceProfileId
        ? voiceProfiles.find(p => p.id === selectedVoiceProfileId)
        : null

      if (linkedProfile) {
        toast.success(`Speaker associato a "${linkedProfile.name}"`, { id: 'rename-speaker' })
      } else {
        toast.success(`Speaker rinominato in "${newSpeakerName.trim()}"`, { id: 'rename-speaker' })
      }

      setEditingSpeaker(null)
      setNewSpeakerName('')
      setSelectedVoiceProfileId(null)
    } catch (error: any) {
      toast.error('Errore: ' + (error.message || 'Salvataggio fallito'), { id: 'rename-speaker' })
    }
  }

  // Open speaker edit modal
  const openSpeakerEdit = (speakerId: string, currentName: string) => {
    // Find the current speaker to get voiceId if exists
    const currentSpeaker = firestoreRecording?.speakers?.find(s => s.id === speakerId)
    setEditingSpeaker({
      id: speakerId,
      name: currentName,
      voiceId: currentSpeaker?.voiceId
    })
    setNewSpeakerName(currentName)
    setSelectedVoiceProfileId(currentSpeaker?.voiceId || null)
  }

  // Fetch voice profiles when modal opens
  useEffect(() => {
    if (!editingSpeaker || !authUser?.id) return

    voiceProfilesService.getAll(authUser.id).then(profiles => {
      setVoiceProfiles(profiles)
    }).catch(err => {
      console.error('Error fetching voice profiles:', err)
    })
  }, [editingSpeaker, authUser?.id])

  const filteredTranscript = searchQuery
    ? recording.transcript.filter(block =>
        block.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
        block.speaker.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : recording.transcript

  const highlightText = (text: string) => {
    if (!searchQuery) return text
    const regex = new RegExp(`(${searchQuery})`, 'gi')
    return text.replace(regex, '<mark class="bg-yellow-200 px-0.5 rounded">$1</mark>')
  }

  // Loading state
  if (isLoadingRecording) {
    return (
      <div className="h-[calc(100vh-3.5rem)] flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-slate-400" />
          <p className="text-slate-500 font-medium">Caricamento registrazione...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (loadError) {
    return (
      <div className="h-[calc(100vh-3.5rem)] flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
            <X className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-900">Errore</h2>
          <p className="text-slate-500">{loadError}</p>
          <button
            onClick={() => navigateBack(-1)}
            className="mt-4 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            Torna indietro
          </button>
        </div>
      </div>
    )
  }

  // Get status badge based on Firestore status
  const getStatusBadge = () => {
    if (!firestoreRecording) return null

    switch (firestoreRecording.status) {
      case 'completed':
        return (
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
            Analisi Completata
          </span>
        )
      case 'processing':
      case 'transcribing':
      case 'analyzing':
        return (
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200 flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            {firestoreRecording.status === 'transcribing' ? 'Trascrizione...' :
             firestoreRecording.status === 'analyzing' ? 'Analisi AI...' : 'Elaborazione...'}
          </span>
        )
      case 'error':
        return (
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 border border-red-200">
            Errore
          </span>
        )
      default:
        return null
    }
  }

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col bg-slate-50">

      {/* Header Strumentale (Toolbar) */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between shadow-sm z-10">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigateBack(-1)}
            className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-xl font-bold text-slate-900">{recording.title}</h1>
              {getStatusBadge()}
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {recording.date} • {recording.duration}</span>
              <span className="flex items-center gap-1"><User className="h-3 w-3" /> {recording.participants} Partecipanti</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {showSearch ? (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca nella trascrizione..."
                className="pl-9 pr-8 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-200 w-64"
                autoFocus
              />
              <button
                onClick={() => { setShowSearch(false); setSearchQuery('') }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowSearch(true)}
              className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
              title="Cerca nella trascrizione"
            >
              <Search className="h-5 w-5" />
            </button>
          )}
          <button
            onClick={handleCopyTranscript}
            className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
            title="Copia trascrizione"
          >
            <Copy className="h-5 w-5" />
          </button>
          <div className="h-6 w-px bg-slate-200 mx-1" />
          <button
            onClick={handleExportPDF}
            disabled={isExporting}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 shadow-sm disabled:opacity-50"
          >
            {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export PDF
          </button>
          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 shadow-md"
          >
            <Share2 className="h-4 w-4" />
            Condividi
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">

        {/* Colonna Sinistra: Trascrizione Interattiva */}
        <div className="flex-1 flex flex-col min-w-0 bg-white">
          {/* Hidden Audio Element */}
          {firestoreRecording?.audioUrl && (
            <audio
              ref={audioRef}
              src={firestoreRecording.audioUrl}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={handleAudioEnded}
              onError={() => setAudioError('Errore nel caricamento dell\'audio')}
              preload="metadata"
            />
          )}

          {/* Audio Player */}
          <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center gap-4">
            <button
              onClick={togglePlayPause}
              disabled={!firestoreRecording?.audioUrl || !!audioError}
              className={`h-10 w-10 flex items-center justify-center rounded-full text-white transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                isPlaying ? 'bg-red-500 hover:bg-red-600' : 'bg-slate-900 hover:bg-slate-800'
              }`}
              title={audioError || (firestoreRecording?.audioUrl ? 'Riproduci/Pausa' : 'Audio non disponibile')}
            >
              {isPlaying ? <Pause className="h-4 w-4 fill-current" /> : <Play className="h-4 w-4 fill-current ml-0.5" />}
            </button>
            <div className="flex-1">
              {/* Progress Bar */}
              <div
                className="h-2 bg-slate-200 rounded-full cursor-pointer relative overflow-hidden"
                onClick={handleSeek}
              >
                <div
                  className="h-full bg-slate-900 rounded-full transition-all duration-100"
                  style={{ width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' }}
                />
              </div>
              {/* Waveform Visual */}
              <div className="flex items-center gap-0.5 h-6 mt-2 opacity-40">
                {[...Array(80)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-0.5 rounded-full transition-all duration-100 ${
                      duration > 0 && i < (currentTime / duration) * 80 ? 'bg-slate-900' : 'bg-slate-400'
                    }`}
                    style={{ height: `${Math.max(15, Math.sin(i * 0.3) * 50 + 50)}%` }}
                  />
                ))}
              </div>
              {audioError && (
                <p className="text-xs text-red-500 mt-1">{audioError}</p>
              )}
            </div>
            <button
              onClick={toggleMute}
              className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
              title={isMuted ? 'Attiva audio' : 'Muta'}
            >
              {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
            <span className="text-xs font-mono text-slate-500 min-w-[100px] text-right">
              {formatTime(Math.floor(currentTime))} / {formatTime(Math.floor(duration))}
            </span>
          </div>

          {/* Search Results Count */}
          {searchQuery && (
            <div className="px-6 py-2 bg-yellow-50 border-b border-yellow-100 text-sm text-yellow-800">
              {filteredTranscript.length} risultat{filteredTranscript.length === 1 ? 'o' : 'i'} per "{searchQuery}"
            </div>
          )}

          {/* Transcript Scroll Area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {filteredTranscript.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Nessun risultato trovato</p>
              </div>
            ) : (
              filteredTranscript.map((block) => (
                <div
                  key={block.id}
                  className="group flex gap-4 hover:bg-slate-50 p-3 rounded-lg -mx-3 transition-colors cursor-pointer"
                  onClick={() => {
                    const timeParts = block.time.split(':')
                    const seconds = parseInt(timeParts[0]) * 60 + parseInt(timeParts[1])
                    setCurrentTime(seconds)
                    toast(`Vai a ${block.time}`, { icon: '⏱️', duration: 1500 })
                  }}
                >
                  <div className="flex-shrink-0 w-12 text-xs font-mono text-slate-400 pt-1 group-hover:text-blue-600 transition-colors">
                    {block.time}
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          openSpeakerEdit(block.speakerId, block.speaker)
                        }}
                        className={`text-xs font-bold px-2 py-0.5 rounded ${block.color} hover:ring-2 hover:ring-slate-400 transition-all cursor-pointer flex items-center gap-1`}
                        title={block.hasVoiceProfile ? "Profilo vocale collegato - clicca per modificare" : "Clicca per rinominare speaker"}
                      >
                        {block.hasVoiceProfile && <Fingerprint className="h-3 w-3" />}
                        {block.speaker}
                        <Edit3 className="h-3 w-3 opacity-50" />
                      </button>
                    </div>
                    <p
                      className="text-slate-700 leading-relaxed text-base"
                      dangerouslySetInnerHTML={{ __html: highlightText(block.text) }}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Colonna Destra: AI Intelligence Panel */}
        <div className="w-96 bg-slate-50 border-l border-slate-200 flex flex-col overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-slate-200 bg-white">
            <button
              onClick={() => setActiveTab('summary')}
              className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'summary' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
              AI Insights
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-2 ${activeTab === 'notes' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
              <MessageSquare className="h-4 w-4" /> Note Private
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-6">

            {activeTab === 'summary' ? (
              <>
                {/* Executive Summary Card */}
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-3 text-slate-800 font-semibold">
                    <FileText className="h-4 w-4 text-slate-600" />
                    <h3>Executive Summary</h3>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {recording.summary}
                  </p>
                </div>

                {/* Action Items */}
                {actionItems.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3 px-1">
                      <div className="flex items-center gap-2 text-slate-800 font-semibold">
                        <CheckSquare className="h-4 w-4 text-emerald-600" />
                        <h3>Action Items Rilevati</h3>
                      </div>
                      <span className="text-xs text-slate-500">
                        {actionItems.filter(i => i.completed).length}/{actionItems.length} completati
                      </span>
                    </div>
                    <div className="space-y-2">
                      {actionItems.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => toggleActionItem(item.id)}
                          className={`bg-white p-3 rounded-lg border shadow-sm flex items-start gap-3 transition-all cursor-pointer ${
                            item.completed
                              ? 'border-emerald-200 bg-emerald-50/50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className={`mt-0.5 h-5 w-5 rounded flex items-center justify-center transition-all ${
                            item.completed
                              ? 'bg-emerald-500 text-white'
                              : 'border-2 border-slate-300 text-transparent hover:border-slate-400'
                          }`}>
                            <CheckIcon className="h-3 w-3" />
                          </div>
                          <div className="flex-1">
                            <p className={`text-sm font-medium transition-all ${
                              item.completed ? 'text-slate-500 line-through' : 'text-slate-700'
                            }`}>{item.text}</p>
                            <div className="flex items-center gap-2 mt-1.5">
                              <span className="text-[10px] font-bold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded uppercase tracking-wide">{item.owner}</span>
                              <span className="text-[10px] text-slate-400">Entro {item.due}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sentiment Analysis Widget */}
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-4 text-slate-800 font-semibold">
                    <BarChart3 className="h-4 w-4 text-purple-600" />
                    <h3>Analisi Sentiment</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between text-xs text-slate-600 mb-1">
                      <span>Positivo</span>
                      <span className="font-bold">{recording.sentiment}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          recording.sentiment >= 70 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' :
                          recording.sentiment >= 50 ? 'bg-gradient-to-r from-amber-400 to-amber-500' :
                          'bg-gradient-to-r from-red-400 to-red-500'
                        }`}
                        style={{ width: `${recording.sentiment}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      {recording.sentiment >= 70
                        ? 'Il tono della riunione è stato costruttivo e focalizzato sulle soluzioni.'
                        : recording.sentiment >= 50
                        ? 'Tono neutro con alcuni punti di discussione.'
                        : 'Rilevate alcune tensioni durante la discussione.'}
                    </p>
                  </div>
                </div>

                {/* Key Topics */}
                {recording.topics.length > 0 && (
                  <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-3 text-slate-800 font-semibold">
                      <Sparkles className="h-4 w-4 text-amber-500" />
                      <h3>Topic Principali</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {recording.topics.map((topic) => (
                        <span
                          key={topic}
                          onClick={() => {
                            setSearchQuery(topic)
                            setShowSearch(true)
                          }}
                          className="px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded-full cursor-pointer hover:bg-slate-200 transition-colors"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Notes Tab */
              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-800">Le tue note</h3>
                    <button
                      onClick={handleSaveNotes}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Salva
                    </button>
                  </div>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Scrivi le tue note private qui... Queste note sono visibili solo a te."
                    className="w-full p-4 text-sm text-slate-700 border-none focus:ring-0 resize-none min-h-[300px]"
                  />
                </div>
                <p className="text-xs text-slate-400 text-center">
                  Le note vengono salvate automaticamente e sono visibili solo a te.
                </p>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Speaker Rename Modal */}
      {editingSpeaker && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-lg text-slate-900">Modifica Speaker</h3>
              <button
                onClick={() => {
                  setEditingSpeaker(null)
                  setNewSpeakerName('')
                  setSelectedVoiceProfileId(null)
                }}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-500">
                Stai modificando <strong className="text-slate-700">{editingSpeaker.name}</strong>.
              </p>

              {/* Voice Profile Link Section */}
              {voiceProfiles.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <Fingerprint className="h-4 w-4 text-blue-600" />
                      Associa a Voice Library
                    </div>
                    {selectedVoiceProfileId && (
                      <button
                        onClick={() => {
                          setSelectedVoiceProfileId(null)
                          setNewSpeakerName(editingSpeaker.name)
                        }}
                        className="text-xs text-red-500 hover:text-red-600"
                      >
                        Rimuovi
                      </button>
                    )}
                  </div>

                  {selectedVoiceProfileId ? (
                    <div className="flex items-center gap-3 bg-white rounded-lg p-3 border border-blue-200">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Link2 className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {voiceProfiles.find(p => p.id === selectedVoiceProfileId)?.name}
                        </p>
                        <p className="text-xs text-slate-500">Profilo vocale collegato</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {voiceProfiles.map(profile => (
                        <button
                          key={profile.id}
                          onClick={() => {
                            setSelectedVoiceProfileId(profile.id)
                            setNewSpeakerName(profile.name)
                          }}
                          className="w-full flex items-center gap-3 p-2 bg-white rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-all text-left"
                        >
                          <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center">
                            <span className="text-sm font-bold text-slate-600">{profile.name.charAt(0)}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-700">{profile.name}</p>
                            <p className="text-xs text-slate-400">{profile.sampleCount} campioni</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Manual Name Input */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                  {selectedVoiceProfileId ? 'Nome (da profilo)' : 'Nome Speaker'}
                </label>
                <input
                  type="text"
                  value={newSpeakerName}
                  onChange={(e) => {
                    setNewSpeakerName(e.target.value)
                    // If user manually edits, unlink profile
                    if (selectedVoiceProfileId) {
                      const profile = voiceProfiles.find(p => p.id === selectedVoiceProfileId)
                      if (profile && e.target.value !== profile.name) {
                        setSelectedVoiceProfileId(null)
                      }
                    }
                  }}
                  placeholder="Es. Mario Rossi"
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/50 focus:border-slate-400 transition-all"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleRenameSpeaker()
                    if (e.key === 'Escape') {
                      setEditingSpeaker(null)
                      setNewSpeakerName('')
                      setSelectedVoiceProfileId(null)
                    }
                  }}
                />
                {selectedVoiceProfileId && (
                  <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                    <Link2 className="h-3 w-3" />
                    Collegato al profilo vocale - verrà riconosciuto automaticamente
                  </p>
                )}
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button
                onClick={() => {
                  setEditingSpeaker(null)
                  setNewSpeakerName('')
                  setSelectedVoiceProfileId(null)
                }}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleRenameSpeaker}
                disabled={!newSpeakerName.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckIcon className="h-4 w-4" /> Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
